import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Avatar,
  Badge,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { MdEvent, MdAccessTime } from 'react-icons/md';

const MotionBox = motion(Box);

/**
 * Workshop Hero Section
 * Displays title, description, date/time, and instructor info
 */
const WorkshopHero = ({ workshop, onScroll }) => {
  if (!workshop) return null;

  const { title, description, schedule, instructor } = workshop;

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <Box bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)" py={{ base: 16, md: 24 }} color="white">
      <Container maxW="container.lg">
        <MotionBox
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <VStack align="start" spacing={6}>
            {/* Badge */}
            <Badge colorScheme="purple" fontSize="md" px={3} py={1}>
              FREE WORKSHOP
            </Badge>

            {/* Title */}
            <Heading
              as="h1"
              size="2xl"
              fontWeight="900"
              lineHeight="1.2"
              maxW="2xl"
            >
              {title}
            </Heading>

            {/* Description */}
            <Text fontSize="lg" maxW="2xl" opacity={0.95} lineHeight="1.6">
              {description}
            </Text>

            {/* Date & Time */}
            <HStack spacing={8} flexWrap="wrap">
              <HStack>
                <MdEvent size={20} />
                <VStack spacing={0} align="start">
                  <Text fontWeight="bold" fontSize="sm">
                    Date
                  </Text>
                  <Text fontSize="md">{formatDate(schedule.date)}</Text>
                </VStack>
              </HStack>

              <HStack>
                <MdAccessTime size={20} />
                <VStack spacing={0} align="start">
                  <Text fontWeight="bold" fontSize="sm">
                    Time
                  </Text>
                  <Text fontSize="md">
                    {formatTime(schedule.startTime)} - {formatTime(schedule.endTime)} {schedule.timezone}
                  </Text>
                </VStack>
              </HStack>
            </HStack>

            {/* Instructor */}
            {instructor && (
              <HStack spacing={4} mt={4}>
                {instructor.image && (
                  <Avatar
                    name={instructor.name}
                    src={instructor.image}
                    size="md"
                  />
                )}
                <VStack spacing={0} align="start">
                  <Text fontWeight="bold">{instructor.name}</Text>
                  <Text fontSize="sm" opacity={0.9}>
                    {instructor.role}
                  </Text>
                </VStack>
              </HStack>
            )}

            {/* CTA */}
            <Button
              size="lg"
              bg="white"
              color="purple.600"
              fontWeight="bold"
              _hover={{ transform: 'translateY(-2px)', boxShadow: '0 8px 16px rgba(0,0,0,0.2)' }}
              onClick={onScroll}
              mt={4}
            >
              Register Now
            </Button>
          </VStack>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default WorkshopHero;
